/**
 * Cliente de autenticação (Better Auth). Camada fina, igual ao api.ts:
 * nenhuma tela chama `authClient` direto — todas usam as funções daqui.
 *
 * `baseURL` já aponta para `/api/auth`, então o Better Auth não acrescenta o
 * basePath de novo. Mesma origem em dev por causa do proxy do Vite: cookie de
 * sessão sem CORS.
 */
import { createAuthClient } from 'better-auth/react';

import { API_ORIGIN } from './api';
import { AuthRequestError } from './errors';

/**
 * `baseURL` do Better Auth exige PROTOCOLO: internamente ele faz `new URL(...)`
 * e um caminho relativo como "/api/auth" derruba o módulo no import
 * (`Invalid base URL`). Então, na mesma origem, não se passa `baseURL` — o
 * próprio cliente resolve `window.location.origin + /api/auth`, que é o
 * caminho onde a api monta o Better Auth.
 */
const clientOptions: { baseURL?: string } = {};
if (API_ORIGIN.length > 0) clientOptions.baseURL = API_ORIGIN;

export const authClient = createAuthClient(clientOptions);

/* ========================================================================== */
/*  Ponte entre o `{ data, error }` do Better Auth e `throw`                   */
/* ========================================================================== */

interface AuthEnvelope {
  error?: { message?: string; code?: string; status?: number } | null;
}

/**
 * O Better Auth devolve erro como VALOR, não como exceção. Se o chamador
 * esquecer de olhar `error`, a tela mostra sucesso em cima de uma falha —
 * por isso tudo passa por aqui e vira `throw`.
 */
async function run(call: PromiseLike<AuthEnvelope>): Promise<void> {
  const result = await call;
  const error = result?.error;
  if (error) {
    throw new AuthRequestError(error.message ?? '', error.code ?? null, error.status ?? null);
  }
}

/* ========================================================================== */
/*  Operações                                                                  */
/* ========================================================================== */

export interface SignInInput {
  email: string;
  password: string;
  rememberMe: boolean;
}

export function signInWithPassword({ email, password, rememberMe }: SignInInput): Promise<void> {
  return run(authClient.signIn.email({ email, password, rememberMe }));
}

export interface SignUpInput {
  name: string;
  email: string;
  password: string;
}

export function signUpWithPassword({ name, email, password }: SignUpInput): Promise<void> {
  return run(
    authClient.signUp.email({
      name,
      email,
      password,
      callbackURL: `${window.location.origin}/verificar-email`,
    }),
  );
}

export function signOut(): Promise<void> {
  return run(authClient.signOut());
}

/**
 * No Better Auth 1.7 o método chama-se `requestPasswordReset` (endpoint
 * `POST /request-password-reset`). O antigo `forgetPassword` saiu do cliente.
 */
export function requestPasswordReset(email: string): Promise<void> {
  return run(
    authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/redefinir-senha`,
    }),
  );
}

export function resetPassword(input: { token: string; password: string }): Promise<void> {
  return run(authClient.resetPassword({ token: input.token, newPassword: input.password }));
}

export function verifyEmail(token: string): Promise<void> {
  return run(authClient.verifyEmail({ query: { token } }));
}

export function resendVerificationEmail(email: string): Promise<void> {
  return run(
    authClient.sendVerificationEmail({
      email,
      callbackURL: `${window.location.origin}/verificar-email`,
    }),
  );
}

export function updateProfile(input: { name: string }): Promise<void> {
  return run(authClient.updateUser({ name: input.name }));
}
